import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ArrowLeft, 
    RefreshCw, 
    Armchair, 
    ChevronRight, 
    Info,
    Sparkles
} from 'lucide-react';
import { fetchBookedSeats } from '../api/api';

// 1. EXACTLY MATCHING FLUTTER SEAT CONFIG (Colors & Prices)
const seatPriceConfig = {
  'A': { price: 0, label: 'Booked', color: '#9CA3AF' },
  'B': { price: 0, label: 'Booked', color: '#9CA3AF' },
  'C': { price: 0, label: 'Booked', color: '#9CA3AF' },
  'D': { price: 0, label: 'Booked', color: '#9CA3AF' },
  'E': { price: 700, label: '₹700', color: '#C5A059' }, // Gold
  'F': { price: 700, label: '₹700', color: '#C5A059' }, // Gold
  'G': { price: 500, label: '₹500', color: '#8B4513' }, // Brown
  'H': { price: 500, label: '₹500', color: '#8B4513' }, // Brown
  'I': { price: 500, label: '₹500', color: '#8B4513' }, // Brown
  'J': { price: 500, label: '₹500', color: '#8B4513' }, // Brown
  'K': { price: 500, label: '₹500', color: '#8B4513' }, // Brown
  'L': { price: 300, label: '₹300', color: '#5F4236' }, // Darker Brown
  'M': { price: 300, label: '₹300', color: '#5F4236' },
  'N': { price: 300, label: '₹300', color: '#5F4236' },
  'O': { price: 300, label: '₹300', color: '#5F4236' },
  'P': { price: 200, label: '₹200', color: '#3E2723' }, // Deep Brown
  'Q': { price: 200, label: '₹200', color: '#3E2723' },
  'R': { price: 200, label: '₹200', color: '#3E2723' },
  'S': { price: 200, label: '₹200', color: '#3E2723' },
  'T': { price: 200, label: '₹200', color: '#3E2723' },
};

// 2. EXACTLY MATCHING FLUTTER LAYOUT DATA
const generateSeatLayout = () => {
    const layout = {
        'A': [[1, 12], [13, 24], [25, 36]],
        'B': [[1, 13], [14, 25], [26, 38]],
        'C': [[1, 13], [14, 26], [27, 39]],
        'D': [[1, 14], [15, 28], [29, 42]],
        'E': [[1, 14], [15, 28], [29, 42]],
        'F': [[1, 15], [16, 30], [31, 45]],
        'G': [[1, 15], [16, 30], [31, 45]],
        'H': [[1, 16], [17, 32], [33, 48]],
        'I': [[1, 16], [17, 32], [33, 48]],
        'J': [[1, 17], [18, 34], [35, 51]],
        'K': [[1, 13], [14, 26], [27, 39], [40, 52]],
        'L': [[1, 14], [15, 28], [29, 42], [43, 56]],
        'M': [[1, 14], [15, 28], [29, 42], [43, 56]],
        'N': [[1, 14], [15, 28], [29, 42], [43, 56]],
        'O': [[1, 15], [16, 30], [31, 45], [46, 60]],
        'P': [[1, 15], [16, 30], [31, 45], [46, 60]],
        'Q': [[1, 16], [17, 32], [33, 48], [49, 64]],
        'R': [[1, 16], [17, 32], [33, 48], [49, 64]],
        'S': [[1, 16], [17, 32], [33, 48], [49, 64]],
        'T': [[1, 12], [13, 24], [25, 36], [37, 48], [49, 60], [61, 72]],
    };

    const rows = [];
    for (const [rowLabel, blockRanges] of Object.entries(layout)) {
        const row = [];
        for (let i = 0; i < blockRanges.length; i++) {
            if (i !== 0) row.push(null); // aisle
            const [start, end] = blockRanges[i];
            for (let seat = start; seat <= end; seat++) {
                row.push(`${rowLabel}${seat}`);
            }
        }
        rows.push({ label: rowLabel, seats: row });
    }
    return rows.reverse(); // 'T' at top, 'A' at bottom (stage)
};

const SeatSelectionScreen = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { event } = location.state || {};

    const [bookedSeats, setBookedSeats] = useState(new Set());
    const [selectedSeats, setSelectedSeats] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const containerRef = useRef(null);

    const seatLayout = useMemo(() => generateSeatLayout(), []);

    const getAutoBookedSeats = (layout) => {
        const seats = new Set();
        const len = layout.length;
        for (let i = len - 4; i < len; i++) {
            if (i >= 0 && i < len) {
                layout[i].seats.forEach(seatId => {
                    if (seatId) seats.add(seatId);
                });
            }
        }
        return seats;
    };

    useEffect(() => {
        if (!event) {
            navigate('/');
            return;
        }
        setBookedSeats(getAutoBookedSeats(seatLayout));
        loadBookedSeatsFromApi();
    }, [event, seatLayout, navigate]);

    const loadBookedSeatsFromApi = async () => {
        setLoading(true);
        try {
            const data = await fetchBookedSeats(event.id);
            if (data.success === 1 && Array.isArray(data.booked_seats)) {
                const apiBooked = data.booked_seats.map(String);
                setBookedSeats(prev => new Set([...prev, ...apiBooked]));
            }
        } catch (err) {
            console.error("Seat fetch error:", err);
        } finally {
            setLoading(false);
        }
    };

    const toggleSeat = (seatId) => {
        if (bookedSeats.has(seatId)) return;
        setSelectedSeats(prev => {
            const next = new Set(prev);
            if (next.has(seatId)) next.delete(seatId);
            else next.add(seatId);
            return next;
        });
    };

    const totalAmount = useMemo(() => {
        let sum = 0;
        selectedSeats.forEach(seatId => {
            const rowChar = seatId[0].toUpperCase();
            const price = seatPriceConfig[rowChar]?.price || 0;
            sum += price;
        });
        return sum;
    }, [selectedSeats]);

    const seatCount = selectedSeats.size;

    if (!event) return null;

    const renderSeat = (seatId) => {
        if (!seatId) {
            return <div key={Math.random()} className="w-full h-full" />;
        }
        
        const isBooked = bookedSeats.has(seatId);
        const isSelected = selectedSeats.has(seatId);
        const rChar = seatId[0].toUpperCase();
        const config = seatPriceConfig[rChar];
        const seatNum = seatId.replace(/[^0-9]/g, '');

        return (
            <button
                key={seatId}
                disabled={isBooked}
                onClick={() => toggleSeat(seatId)}
                style={!isBooked && !isSelected ? { borderBottomWidth: '2px', borderBottomColor: config?.color } : {}}
                className={`
                    w-full aspect-square rounded-[2px] sm:rounded-[3px] md:rounded-md flex items-center justify-center text-[4px] sm:text-[6px] md:text-[8px] font-black transition-all
                    ${isBooked ? 'bg-secondary/10 text-secondary/20 cursor-not-allowed' :
                      isSelected ? 'bg-primary text-white shadow-lg scale-110 z-10' :
                      'bg-white border border-primary/10 hover:border-primary hover:text-primary'}
                `}
            >
                {seatNum}
            </button>
        );
    };

    return (
        <div className="min-h-screen bg-cream pb-44 font-outfit text-secondary relative overflow-hidden">
            {/* Background Decor */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-secondary/5 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2"></div>

            {/* Top Bar */}
            <div className="bg-white/80 backdrop-blur-md border-b border-primary/10 sticky top-0 z-50 px-4 md:px-6 py-4">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <button onClick={() => navigate(-1)} className="flex items-center text-primary hover:text-secondary font-bold transition-all group">
                        <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
                        <span className="text-sm">Back</span>
                    </button>
                    <div className="text-center">
                        <h1 className="text-base md:text-lg font-playfair font-black truncate max-w-[150px] md:max-w-md">{event.title}</h1>
                        <p className="text-[8px] md:text-[10px] text-primary font-black uppercase tracking-[0.2em]">{event.location}</p>
                    </div>
                    <button onClick={loadBookedSeatsFromApi} className={`p-2 text-primary hover:text-secondary transition-all ${loading ? 'animate-spin' : ''}`}>
                        <RefreshCw size={20} />
                    </button>
                </div>
            </div>

            <main className="max-w-7xl mx-auto px-4 md:px-6 mt-6 md:mt-8 relative z-10">
                {/* Selection Summary */}
                <div className="mb-6 md:mb-8">
                    {seatCount > 0 ? (
                        <motion.div 
                            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                            className="bg-secondary p-5 md:p-8 rounded-[2rem] md:rounded-[3rem] flex flex-col md:flex-row items-center justify-between gap-6 shadow-2xl shadow-secondary/10 relative overflow-hidden"
                        >
                            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-2xl"></div>
                            <div className="flex items-center gap-5 relative z-10">
                                <div className="bg-primary/20 p-4 rounded-2xl"><Armchair size={28} className="text-primary" /></div>
                                <div>
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-primary mb-1">Seats Selected</p>
                                    <p className="text-lg md:text-xl font-bold text-white italic">{[...selectedSeats].sort().join(', ')}</p>
                                </div>
                            </div>
                            <div className="text-center md:text-right relative z-10">
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-primary mb-1">Total Contribution</p>
                                <p className="text-2xl md:text-4xl font-black text-white italic">₹{totalAmount.toLocaleString('en-IN')}</p>
                            </div>
                        </motion.div>
                    ) : (
                        <div className="flex items-center justify-center gap-3 text-secondary/40 bg-white/50 backdrop-blur-sm py-5 rounded-[2rem] border border-primary/10 shadow-sm">
                            <Sparkles size={18} className="text-primary/40" />
                            <span className="text-sm font-bold uppercase tracking-widest">Select Your Divine Experience</span>
                        </div>
                    )}
                </div>

                {/* Seating Layout Area */}
                <div className="bg-white rounded-[2.5rem] md:rounded-[4rem] p-6 md:p-16 border border-primary/10 shadow-xl shadow-primary/5">
                    {/* Legend */}
                    <div className="flex flex-wrap justify-center gap-x-6 md:gap-x-10 gap-y-4 mb-10 md:mb-16 w-full">
                        <LegendItem color="#C5A059" label="₹700" />
                        <LegendItem color="#8B4513" label="₹500" />
                        <LegendItem color="#5F4236" label="₹300" />
                        <LegendItem color="#3E2723" label="₹200" />
                        <LegendItem color="#C5A059" label="Selected" isSelected />
                        <LegendItem color="#F1F1F1" label="Booked" isBooked />
                    </div>

                    {/* Scrollable Seat Container */}
                    <div className="max-h-[60vh] overflow-y-auto pb-10 px-2 custom-scrollbar">
                        <div ref={containerRef} className="flex flex-col items-center space-y-2 md:space-y-4 w-full min-w-[600px]">
                            
                            {seatLayout.map((row) => {
                                const totalColumns = row.seats.length;
                                return (
                                    <div key={row.label} className="flex items-center justify-center w-full gap-2 md:gap-4">
                                        <span className="w-4 md:w-8 text-[8px] md:text-[10px] font-black text-secondary/20 text-right flex-shrink-0">{row.label}</span>
                                        <div 
                                            className="grid flex-1 gap-1 md:gap-2"
                                            style={{ gridTemplateColumns: `repeat(${totalColumns}, minmax(0, 1fr))` }}
                                        >
                                            {row.seats.map((seatId) => renderSeat(seatId))}
                                        </div>
                                        <span className="w-4 md:w-8 text-[8px] md:text-[10px] font-black text-secondary/20 text-left flex-shrink-0">{row.label}</span>
                                    </div>
                                );
                            })}

                            {/* Stage Decor */}
                            <div className="mt-16 w-full max-w-3xl">
                                <div className="h-1 w-full bg-gradient-to-r from-transparent via-primary/30 to-transparent rounded-full mb-6"></div>
                                <div className="py-6 md:py-10 bg-cream/50 rounded-[2.5rem] text-center border border-primary/10 relative overflow-hidden group">
                                    <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                    <span className="text-secondary/30 font-black tracking-[1.5em] text-[10px] md:text-xs uppercase ml-[1.5em] relative z-10">THE DIVINE STAGE</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {/* Sticky Bottom Button */}
            <div className="fixed bottom-0 left-0 right-0 p-6 md:p-10 bg-gradient-to-t from-cream via-cream to-transparent z-40">
                <div className="max-w-md mx-auto">
                    <button
                        disabled={loading || seatCount === 0}
                        onClick={() => navigate('/payment', { state: { event, selectedSeats: [...selectedSeats], totalAmount } })}
                        className={`w-full py-5 md:py-6 rounded-[2rem] font-black text-base md:text-lg flex items-center justify-center gap-4 transition-all shadow-2xl transform active:scale-95
                            ${seatCount === 0 ? 'bg-secondary/10 text-secondary/30 cursor-not-allowed shadow-none' : 'bg-primary text-white hover:bg-secondary shadow-primary/20'}`}
                    >
                        <span>{seatCount === 0 ? 'Select Your Seats' : `Book ${seatCount} Experience${seatCount > 1 ? 's' : ''} • ₹${totalAmount}`}</span>
                        <ChevronRight size={24} />
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {loading && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-cream/80 backdrop-blur-md flex items-center justify-center z-[100]">
                        <div className="flex flex-col items-center">
                            <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                            <p className="mt-4 text-[10px] font-black text-primary uppercase tracking-[0.3em]">Checking Availability...</p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const LegendItem = ({ color, label, isBooked, isSelected }) => (
    <div className="flex items-center gap-3">
        <div 
            className={`w-4 h-4 rounded-md shadow-sm ${isBooked ? 'bg-secondary/10 border border-primary/5' : isSelected ? 'bg-primary shadow-primary/20 scale-110' : ''}`}
            style={!isBooked && !isSelected ? { backgroundColor: color, borderBottom: `2px solid rgba(0,0,0,0.1)` } : {}}
        ></div>
        <span className="text-[10px] font-black text-secondary/50 uppercase tracking-widest">{label}</span>
    </div>
);

export default SeatSelectionScreen;
